// =============================================
// Admin Kindergartens Management Page
// Kipikid - מערכת נוכחות ילדים בגנים
// חיבור ל-API אמיתי + שיוך ארגון
// =============================================

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  Search,
  Building2,
  MapPin,
  Phone,
  Users,
  Baby,
  X,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  Clock,
  AlertCircle,
  CheckCircle2,
  Landmark,
  Loader2,
  Wifi,
  Cable,
  Router,
  Tablet,
  Globe,
} from 'lucide-react';
import {
  adminApi,
  type AdminKindergartenDto,
  type CreateKindergartenRequest,
  type UpdateKindergartenRequest,
  type OrganizationDto,
} from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useAdminStore } from '../../stores/adminStore';
import DeleteConfirmModal from '../../components/shared/DeleteConfirmModal';
import PhotoUpload from '../../components/common/PhotoUpload';
import KindergartenMap from '../../components/common/KindergartenMap';
import FormLabel from '../../components/common/FormLabel';
import HelperText from '../../components/common/HelperText';
import { adminKindergartenHelpTexts } from '../../constants/helpTexts';
import { adminColors as colors } from '../../styles/colors';

export default function AdminKindergartens() {
  const { user } = useAuthStore();
  const { selectedOrganizationId } = useAdminStore();
  const [searchParams] = useSearchParams();
  // סינון לפי ארגון: מ-URL param, מהארגון של המשתמש המחובר, או מ-adminStore
  const orgFilter = searchParams.get('org') || user?.organizationPublicId || selectedOrganizationId || undefined;
  const [kindergartens, setKindergartens] = useState<AdminKindergartenDto[]>([]);
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedKindergarten, setSelectedKindergarten] = useState<AdminKindergartenDto | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<{ publicId: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const result = await adminApi.getKindergartens(orgFilter);
        if (result.success && result.data) {
          setKindergartens(result.data);
          // שם ארגון מהגן הראשון (אם יש)
          if (orgFilter && result.data.length > 0 && result.data[0].organizationName) {
            setOrgName(result.data[0].organizationName);
          } else if (orgFilter) {
            // טעינת שם הארגון מ-API ארגונים
            const orgsResult = await adminApi.getOrganizations();
            if (orgsResult.success && orgsResult.data) {
              const org = orgsResult.data.find(o => o.publicId === orgFilter);
              if (org) setOrgName(org.name);
            }
          } else {
            setOrgName('');
          }
        }
      } catch (err) {
        console.error('Failed to load kindergartens:', err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [orgFilter]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleDelete = (publicId: string, name: string) => {
    setDeleteTarget({ publicId, name });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      const result = await adminApi.deleteKindergarten(deleteTarget.publicId);
      if (result.success) {
        setKindergartens(prev => prev.filter(k => k.publicId !== deleteTarget.publicId));
        setSelectedKindergarten(null);
        showSuccess('הגן נמחק בהצלחה');
      }
    } catch (err) {
      console.error('Failed to delete kindergarten:', err);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  const filteredKindergartens = kindergartens.filter(k =>
    k.name.includes(searchQuery) ||
    k.city.includes(searchQuery) ||
    k.street.includes(searchQuery) ||
    k.code.includes(searchQuery) ||
    (k.organizationName && k.organizationName.includes(searchQuery))
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
            {orgFilter && orgName ? `גנים - ${orgName}` : 'ניהול גנים'}
          </h1>
          <p style={{ fontSize: '14px', color: colors.textLight }}>
            {kindergartens.length} גנים {orgFilter ? 'בארגון' : 'במערכת'}
          </p>
        </div>
        <motion.button
          onClick={() => setShowAddModal(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            backgroundColor: colors.primary,
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={18} />
          הוסף גן
        </motion.button>
      </div>

      {/* Success Message */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 16px',
              backgroundColor: colors.successLight,
              borderRadius: '10px',
            }}
          >
            <CheckCircle2 size={18} color={colors.success} />
            <span style={{ fontSize: '14px', color: colors.success }}>{successMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search */}
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="חיפוש לפי שם, עיר, רחוב, קוד או ארגון..."
          style={{
            width: '100%',
            padding: '14px 44px 14px 14px',
            fontSize: '15px',
            border: `2px solid ${colors.border}`,
            borderRadius: '12px',
            outline: 'none',
            boxSizing: 'border-box',
            direction: 'rtl',
          }}
        />
        <Search
          size={20}
          color={colors.textLight}
          style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}
        />
      </div>

      {/* Kindergartens List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: colors.textLight }}>
          טוען גנים...
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filteredKindergartens.map((kg, index) => (
            <KindergartenCard
              key={kg.publicId}
              kindergarten={kg}
              index={index}
              onClick={() => setSelectedKindergarten(kg)}
            />
          ))}
          {filteredKindergartens.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}>
              <Building2 size={48} color={colors.textLight} style={{ margin: '0 auto 16px' }} />
              <p style={{ fontSize: '16px', color: colors.text, fontWeight: '500' }}>
                {searchQuery ? 'לא נמצאו גנים' : 'אין גנים עדיין'}
              </p>
              <p style={{ fontSize: '14px', color: colors.textLight, marginTop: '4px' }}>
                {searchQuery ? 'נסו חיפוש אחר' : 'לחצו על "הוסף גן" להוספת גן ראשון'}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Add Kindergarten Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddKindergartenModal
            onClose={() => setShowAddModal(false)}
            onCreated={(newKg) => {
              setKindergartens(prev => [...prev, newKg]);
              setShowAddModal(false);
              showSuccess(`גן "${newKg.name}" נוצר בהצלחה`);
            }}
            orgFilter={orgFilter}
          />
        )}
      </AnimatePresence>

      {/* Kindergarten Details Modal */}
      <AnimatePresence>
        {selectedKindergarten && (
          <KindergartenDetailsModal
            kindergarten={selectedKindergarten}
            onClose={() => setSelectedKindergarten(null)}
            onDelete={(publicId) => handleDelete(publicId, selectedKindergarten.name)}
            onUpdated={(updated) => {
              setKindergartens(prev => prev.map(k => k.publicId === updated.publicId ? updated : k));
              setSelectedKindergarten(updated);
              showSuccess('הגן עודכן בהצלחה');
            }}
          />
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        itemName={deleteTarget?.name || ''}
        warningMessage="פעולה זו תמחק את הגן וכל הנתונים הקשורים אליו. לא ניתן לשחזר."
        isLoading={isDeleting}
      />
    </div>
  );
}

function KindergartenCard({ kindergarten, index, onClick }: { kindergarten: AdminKindergartenDto; index: number; onClick: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      }}
    >
      {/* Icon */}
      <div style={{
        width: '56px',
        height: '56px',
        borderRadius: '14px',
        backgroundColor: colors.primaryLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Building2 size={24} color={colors.primary} />
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: colors.text }}>
            {kindergarten.name}
          </h3>
          <span style={{
            padding: '2px 8px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '500',
            backgroundColor: colors.secondaryLight,
            color: colors.secondary,
          }}>
            {kindergarten.code}
          </span>
        </div>
        <p style={{ fontSize: '13px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '6px' }}>
          <MapPin size={14} />
          {kindergarten.street} {kindergarten.houseNumber}, {kindergarten.city}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '6px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Baby size={12} />
            {kindergarten.childrenCount} ילדים
          </span>
          <span style={{ fontSize: '12px', color: colors.textLight, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Users size={12} />
            {kindergarten.staffCount} צוות
          </span>
          {kindergarten.organizationName && (
            <span style={{ fontSize: '12px', color: colors.primary, display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Landmark size={12} />
              {kindergarten.organizationName}
            </span>
          )}
        </div>
      </div>

      <ChevronLeft size={20} color={colors.textLight} />
    </motion.div>
  );
}

function KindergartenDetailsModal({
  kindergarten,
  onClose,
  onDelete,
  onUpdated,
}: {
  kindergarten: AdminKindergartenDto;
  onClose: () => void;
  onDelete: (publicId: string) => void;
  onUpdated: (updated: AdminKindergartenDto) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(kindergarten.name);
  const [editCity, setEditCity] = useState(kindergarten.city);
  const [editStreet, setEditStreet] = useState(kindergarten.street);
  const [editHouseNumber, setEditHouseNumber] = useState(kindergarten.houseNumber || '');
  const [editPhone, setEditPhone] = useState(kindergarten.phone || '');
  const [editMaxChildren, setEditMaxChildren] = useState(kindergarten.maxChildren);
  const [editPhone2, setEditPhone2] = useState(kindergarten.phone2 || '');
  const [editOrgPublicId, setEditOrgPublicId] = useState(kindergarten.organizationPublicId || '');
  const [editGpsLat, setEditGpsLat] = useState<number | undefined>(kindergarten.gpsLatitude ?? undefined);
  const [editGpsLng, setEditGpsLng] = useState<number | undefined>(kindergarten.gpsLongitude ?? undefined);
  // אינטרנט ורשת
  const [editNetworkType, setEditNetworkType] = useState(kindergarten.networkType || 0);
  const [editNetworkName, setEditNetworkName] = useState(kindergarten.networkName || '');
  const [editNetworkPassword, setEditNetworkPassword] = useState(kindergarten.networkPassword || '');
  const [editInternetProvider, setEditInternetProvider] = useState(kindergarten.internetProvider || '');
  const [editInternetPlanDetails, setEditInternetPlanDetails] = useState(kindergarten.internetPlanDetails || '');
  // ציוד
  const [editRouterModel, setEditRouterModel] = useState(kindergarten.routerModel || '');
  const [editTabletModel, setEditTabletModel] = useState(kindergarten.tabletModel || '');
  // שעות פעילות
  const [editOpenTime, setEditOpenTime] = useState(kindergarten.openTime || '07:00');
  const [editCloseTime, setEditCloseTime] = useState(kindergarten.closeTime || '16:00');
  const [editDisplaySwitchTime, setEditDisplaySwitchTime] = useState(kindergarten.displaySwitchTime || '09:00');
  const [editLatestArrivalTime, setEditLatestArrivalTime] = useState(kindergarten.latestArrivalTime || '09:30');
  const [newPhotosBase64, setNewPhotosBase64] = useState<string[]>([]);
  const [newPhotoPreviews, setNewPhotoPreviews] = useState<string[]>([]);
  const [removePhotoUrls, setRemovePhotoUrls] = useState<string[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [gpsManuallyChanged, setGpsManuallyChanged] = useState(false);

  // Lightbox keyboard navigation
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e: KeyboardEvent) => {
      const total = kindergarten.photoUrls?.length || 0;
      if (e.key === 'Escape') setLightboxIndex(null);
      else if (e.key === 'ArrowRight' && total > 1) setLightboxIndex(prev => prev !== null ? (prev + 1) % total : 0);
      else if (e.key === 'ArrowLeft' && total > 1) setLightboxIndex(prev => prev !== null ? (prev - 1 + total) % total : 0);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex, kindergarten.photoUrls]);

  useEffect(() => {
    if (isEditing) {
      adminApi.getOrganizations().then(res => {
        if (res.success && res.data) setOrganizations(res.data);
      });
    }
  }, [isEditing]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const data: UpdateKindergartenRequest = {
        name: editName,
        city: editCity,
        street: editStreet,
        houseNumber: editHouseNumber || undefined,
        phone: editPhone || undefined,
        phone2: editPhone2 || undefined,
        maxChildren: editMaxChildren,
        // שליחת GPS רק אם המשתמש שינה ידנית — אחרת הbackend יבצע geocoding אוטומטי בשינוי כתובת
        ...(gpsManuallyChanged ? { gpsLatitude: editGpsLat, gpsLongitude: editGpsLng } : {}),
        // אינטרנט ורשת
        networkType: editNetworkType,
        networkName: editNetworkName || undefined,
        networkPassword: editNetworkPassword || undefined,
        internetProvider: editInternetProvider || undefined,
        internetPlanDetails: editInternetPlanDetails || undefined,
        // ציוד
        routerModel: editRouterModel || undefined,
        tabletModel: editTabletModel || undefined,
        // שעות פעילות
        openTime: editOpenTime || undefined,
        closeTime: editCloseTime || undefined,
        displaySwitchTime: editDisplaySwitchTime || undefined,
        latestArrivalTime: editLatestArrivalTime || undefined,
      };

      if (newPhotosBase64.length > 0) {
        data.photosBase64 = newPhotosBase64;
      }
      if (removePhotoUrls.length > 0) {
        data.removePhotoUrls = removePhotoUrls;
      }

      if (!editOrgPublicId && kindergarten.organizationPublicId) {
        data.clearOrganization = true;
      } else if (editOrgPublicId && editOrgPublicId !== kindergarten.organizationPublicId) {
        data.organizationPublicId = editOrgPublicId;
      }

      const result = await adminApi.updateKindergarten(kindergarten.publicId, data);
      if (result.success && result.data) {
        onUpdated(result.data);
        setIsEditing(false);
      } else {
        setError(result.message || 'שגיאה בעדכון');
      }
    } catch {
      setError('שגיאה בעדכון הגן');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '24px',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
        dir="rtl"
      >
        {/* Header - sticky */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: '20px', position: 'sticky', top: '-24px', paddingTop: '24px',
          backgroundColor: 'white', zIndex: 10, marginTop: '-24px', borderRadius: '20px 20px 0 0',
        }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text, marginBottom: '4px' }}>
              {kindergarten.name}
            </h2>
            <span style={{
              display: 'inline-block',
              padding: '4px 10px',
              borderRadius: '8px',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: colors.secondaryLight,
              color: colors.secondary,
            }}>
              קוד: {kindergarten.code}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: colors.background,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <X size={18} color={colors.textLight} />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        {isEditing ? (
          /* Edit Mode */
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            <div>
              <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>שם הגן</label>
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>עיר</label>
                <input type="text" value={editCity} onChange={(e) => setEditCity(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>רחוב</label>
                <input type="text" value={editStreet} onChange={(e) => setEditStreet(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ width: '70px' }}>
                <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>מספר</label>
                <input type="text" value={editHouseNumber} onChange={(e) => setEditHouseNumber(e.target.value)} style={{ ...inputStyle, textAlign: 'center' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>טלפון</label>
                <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>טלפון נוסף</label>
                <input type="tel" value={editPhone2} onChange={(e) => setEditPhone2(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ width: '100px' }}>
                <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>מקסימום ילדים</label>
                <input type="number" value={editMaxChildren} onChange={(e) => setEditMaxChildren(parseInt(e.target.value) || 35)} style={{ ...inputStyle, textAlign: 'center' }} />
              </div>
            </div>

            {/* אינטרנט ורשת */}
            <div style={{ padding: '12px', backgroundColor: '#f0f9ff', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: colors.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Wifi size={14} color={colors.primary} /> אינטרנט ורשת
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>סוג רשת</label>
                  <select value={editNetworkType} onChange={(e) => setEditNetworkType(Number(e.target.value))} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value={0}>לא מוגדר</option>
                    <option value={1}>אלחוטי (WiFi)</option>
                    <option value={2}>קווי</option>
                    <option value={3}>משולב</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>ספק אינטרנט</label>
                  <input type="text" value={editInternetProvider} onChange={(e) => setEditInternetProvider(e.target.value)} placeholder="בזק, הוט..." style={inputStyle} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>שם רשת</label>
                  <input type="text" value={editNetworkName} onChange={(e) => setEditNetworkName(e.target.value)} placeholder="SSID" style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>סיסמת רשת</label>
                  <input type="text" value={editNetworkPassword} onChange={(e) => setEditNetworkPassword(e.target.value)} style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>פרטי מנוי</label>
                <input type="text" value={editInternetPlanDetails} onChange={(e) => setEditInternetPlanDetails(e.target.value)} placeholder="חבילת 100Mbps..." style={inputStyle} />
              </div>
            </div>

            {/* ציוד */}
            <div style={{ padding: '12px', backgroundColor: '#faf5ff', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: colors.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Router size={14} color="#8b5cf6" /> ציוד
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>דגם ראוטר</label>
                  <input type="text" value={editRouterModel} onChange={(e) => setEditRouterModel(e.target.value)} placeholder="TP-Link, Mikrotik..." style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>דגם טאבלט</label>
                  <input type="text" value={editTabletModel} onChange={(e) => setEditTabletModel(e.target.value)} placeholder="Samsung Tab A8..." style={inputStyle} />
                </div>
              </div>
            </div>

            {/* שעות פעילות */}
            <div style={{ padding: '12px', backgroundColor: '#fffbeb', borderRadius: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: colors.text, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Clock size={14} color="#f59e0b" /> שעות פעילות
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>שעת פתיחה</label>
                  <input type="time" value={editOpenTime} onChange={(e) => setEditOpenTime(e.target.value)} style={{ ...inputStyle, fontSize: '14px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>שעת סגירה</label>
                  <input type="time" value={editCloseTime} onChange={(e) => setEditCloseTime(e.target.value)} style={{ ...inputStyle, fontSize: '14px' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>שעת החלפת מסך</label>
                  <input type="time" value={editDisplaySwitchTime} onChange={(e) => setEditDisplaySwitchTime(e.target.value)} style={{ ...inputStyle, fontSize: '14px' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>שעת הגעה אחרונה</label>
                  <input type="time" value={editLatestArrivalTime} onChange={(e) => setEditLatestArrivalTime(e.target.value)} style={{ ...inputStyle, fontSize: '14px' }} />
                </div>
              </div>
            </div>

            <div>
              <label style={{ fontSize: '12px', color: colors.textLight, marginBottom: '4px', display: 'block' }}>ארגון</label>
              <select
                value={editOrgPublicId}
                onChange={(e) => setEditOrgPublicId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">ללא ארגון (עצמאי)</option>
                {organizations.map(org => (
                  <option key={org.publicId} value={org.publicId}>
                    {org.name} ({org.organizationTypeName})
                  </option>
                ))}
              </select>
              {/* Warning when organization is changing */}
              {kindergarten.organizationPublicId && editOrgPublicId !== kindergarten.organizationPublicId && (
                <div style={{
                  marginTop: '8px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  backgroundColor: colors.warningLight,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '8px',
                }}>
                  <AlertCircle size={16} color={colors.warning} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p style={{ fontSize: '12px', color: '#92400e', lineHeight: '1.5', margin: 0 }}>
                    שינוי ארגון יעביר את כל צוות הגן לארגון החדש. שיבוצי ימים של הצוות לגנים בארגון הנוכחי יימחקו.
                  </p>
                </div>
              )}
            </div>

            {/* Photo Upload (Edit) - Multi */}
            <PhotoUpload
              mode="multi"
              currentPhotoUrls={(kindergarten.photoUrls || []).filter(u => !removePhotoUrls.includes(u))}
              newPhotoPreviews={newPhotoPreviews}
              onPhotosAdd={(base64List) => {
                setNewPhotosBase64(prev => [...prev, ...base64List]);
                setNewPhotoPreviews(prev => [...prev, ...base64List.map(b => `data:image/jpeg;base64,${b}`)]);
              }}
              onPhotoRemove={(url) => {
                setRemovePhotoUrls(prev => [...prev, url]);
              }}
              onNewPhotoRemove={(index) => {
                setNewPhotosBase64(prev => prev.filter((_, i) => i !== index));
                setNewPhotoPreviews(prev => prev.filter((_, i) => i !== index));
              }}
            />

            {/* Map / GPS (Edit) */}
            <KindergartenMap
              latitude={editGpsLat}
              longitude={editGpsLng}
              name={editName}
              address={`${editStreet} ${editHouseNumber || ''}, ${editCity}`}
              editable
              onLocationSelect={(lat, lng) => {
                setEditGpsLat(lat);
                setEditGpsLng(lng);
                setGpsManuallyChanged(true);
              }}
            />

            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <motion.button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: colors.primary,
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                whileTap={{ scale: saving ? 1 : 0.98 }}
              >
                {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
                {saving ? 'שומר...' : 'שמור'}
              </motion.button>
              <motion.button
                onClick={() => setIsEditing(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: `1px solid ${colors.border}`,
                  backgroundColor: 'white',
                  color: colors.text,
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                ביטול
              </motion.button>
            </div>
          </div>
        ) : (
          /* View Mode */
          <>
            {/* Photos Gallery */}
            {kindergarten.photoUrls && kindergarten.photoUrls.length > 0 && (
              <div style={{
                display: 'flex',
                gap: '8px',
                overflowX: 'auto',
                marginBottom: '16px',
                paddingBottom: '4px',
              }}>
                {kindergarten.photoUrls.map((url, index) => (
                  <div
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    style={{
                      borderRadius: '12px',
                      overflow: 'hidden',
                      flexShrink: 0,
                      width: kindergarten.photoUrls.length === 1 ? '100%' : '160px',
                      height: '160px',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={url}
                      alt={`${kindergarten.name} ${index + 1}`}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Photo Lightbox */}
            <AnimatePresence>
              {lightboxIndex !== null && kindergarten.photoUrls && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  onMouseDown={(e) => { if (e.target === e.currentTarget) setLightboxIndex(null); }}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 200,
                    padding: '20px',
                  }}
                >
                  {/* Close button */}
                  <button
                    onClick={() => setLightboxIndex(null)}
                    style={{
                      position: 'absolute', top: '16px', right: '16px',
                      background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                      width: '40px', height: '40px', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    <X size={22} color="white" />
                  </button>

                  {/* Counter */}
                  <div style={{
                    position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)',
                    color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: '500',
                  }}>
                    {lightboxIndex + 1} / {kindergarten.photoUrls.length}
                  </div>

                  {/* Previous arrow */}
                  {kindergarten.photoUrls.length > 1 && (
                    <button
                      onClick={() => setLightboxIndex(prev => prev !== null ? (prev + 1) % kindergarten.photoUrls.length : 0)}
                      style={{
                        position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                        width: '44px', height: '44px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <ChevronRight size={24} color="white" />
                    </button>
                  )}

                  {/* Next arrow */}
                  {kindergarten.photoUrls.length > 1 && (
                    <button
                      onClick={() => setLightboxIndex(prev => prev !== null ? (prev - 1 + kindergarten.photoUrls.length) % kindergarten.photoUrls.length : 0)}
                      style={{
                        position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
                        background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%',
                        width: '44px', height: '44px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <ChevronLeft size={24} color="white" />
                    </button>
                  )}

                  {/* Image */}
                  <motion.img
                    key={lightboxIndex}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    src={kindergarten.photoUrls[lightboxIndex]}
                    alt={`${kindergarten.name} ${lightboxIndex + 1}`}
                    style={{
                      maxWidth: '90vw', maxHeight: '80vh',
                      objectFit: 'contain', borderRadius: '12px',
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <DetailRow icon={MapPin} label="כתובת" value={`${kindergarten.street} ${kindergarten.houseNumber || ''}, ${kindergarten.city}`} />
              {kindergarten.phone && <DetailRow icon={Phone} label="טלפון" value={kindergarten.phone} />}
              {kindergarten.phone2 && <DetailRow icon={Phone} label="טלפון נוסף" value={kindergarten.phone2} />}
              <DetailRow icon={Baby} label="ילדים" value={`${kindergarten.childrenCount} רשומים (מקסימום ${kindergarten.maxChildren})`} />
              <DetailRow icon={Users} label="צוות" value={`${kindergarten.staffCount} עובדים`} />
              {kindergarten.organizationName ? (
                <DetailRow icon={Landmark} label="ארגון" value={kindergarten.organizationName} />
              ) : (
                <DetailRow icon={Building2} label="ארגון" value="עצמאי (ללא שיוך)" />
              )}

              {/* אינטרנט ורשת */}
              {(kindergarten.networkType > 0 || kindergarten.internetProvider || kindergarten.networkName) && (
                <>
                  {kindergarten.networkType > 0 && (
                    <DetailRow
                      icon={kindergarten.networkType === 2 ? Cable : Wifi}
                      label="סוג רשת"
                      value={kindergarten.networkType === 1 ? 'אלחוטי (WiFi)' : kindergarten.networkType === 2 ? 'קווי' : kindergarten.networkType === 3 ? 'משולב' : ''}
                    />
                  )}
                  {kindergarten.internetProvider && <DetailRow icon={Globe} label="ספק אינטרנט" value={kindergarten.internetProvider} />}
                  {kindergarten.networkName && <DetailRow icon={Wifi} label="שם רשת" value={kindergarten.networkName} />}
                  {kindergarten.internetPlanDetails && <DetailRow icon={Globe} label="פרטי מנוי" value={kindergarten.internetPlanDetails} />}
                </>
              )}

              {/* ציוד */}
              {(kindergarten.routerModel || kindergarten.tabletModel) && (
                <>
                  {kindergarten.routerModel && <DetailRow icon={Router} label="דגם ראוטר" value={kindergarten.routerModel} />}
                  {kindergarten.tabletModel && <DetailRow icon={Tablet} label="דגם טאבלט" value={kindergarten.tabletModel} />}
                </>
              )}

              {/* שעות פעילות */}
              {(kindergarten.openTime || kindergarten.closeTime) && (
                <>
                  <DetailRow icon={Clock} label="שעות פעילות" value={`${kindergarten.openTime || '07:00'} - ${kindergarten.closeTime || '16:00'}`} />
                  <DetailRow icon={Clock} label="החלפת מסך / הגעה אחרונה" value={`${kindergarten.displaySwitchTime || '09:00'} / ${kindergarten.latestArrivalTime || '09:30'}`} />
                </>
              )}
            </div>

            {/* Map */}
            <div style={{ marginBottom: '16px' }}>
              <KindergartenMap
                latitude={kindergarten.gpsLatitude}
                longitude={kindergarten.gpsLongitude}
                name={kindergarten.name}
                address={`${kindergarten.street} ${kindergarten.houseNumber || ''}, ${kindergarten.city}`}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '10px' }}>
              <motion.button
                onClick={() => setIsEditing(true)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: colors.primaryLight,
                  color: colors.primary,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <Edit2 size={16} />
                עריכה
              </motion.button>
              <motion.button
                onClick={() => onDelete(kindergarten.publicId)}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px',
                  backgroundColor: '#fee2e2',
                  color: colors.error,
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
                whileTap={{ scale: 0.98 }}
              >
                <Trash2 size={16} />
                מחיקה
              </motion.button>
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function DetailRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: colors.background, borderRadius: '10px' }}>
      <Icon size={18} color={colors.primary} />
      <div>
        <p style={{ fontSize: '11px', color: colors.textLight }}>{label}</p>
        <p style={{ fontSize: '14px', fontWeight: '500', color: colors.text }}>{value}</p>
      </div>
    </div>
  );
}

function AddKindergartenModal({ onClose, onCreated, orgFilter }: { onClose: () => void; onCreated: (kg: AdminKindergartenDto) => void; orgFilter?: string }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [city, setCity] = useState('');
  const [street, setStreet] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [phone2, setPhone2] = useState('');
  const [maxChildren, setMaxChildren] = useState(35);
  const [gpsLatitude, setGpsLatitude] = useState<number | undefined>(undefined);
  const [gpsLongitude, setGpsLongitude] = useState<number | undefined>(undefined);
  // אינטרנט ורשת
  const [networkType, setNetworkType] = useState(0);
  const [networkName, setNetworkName] = useState('');
  const [networkPassword, setNetworkPassword] = useState('');
  const [internetProvider, setInternetProvider] = useState('');
  const [internetPlanDetails, setInternetPlanDetails] = useState('');
  // ציוד
  const [routerModel, setRouterModel] = useState('');
  const [tabletModel, setTabletModel] = useState('');
  const [photosBase64, setPhotosBase64] = useState<string[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [openTime, setOpenTime] = useState('07:00');
  const [closeTime, setCloseTime] = useState('16:00');
  const [displaySwitchTime, setDisplaySwitchTime] = useState('09:00');
  const [latestArrivalTime, setLatestArrivalTime] = useState('09:30');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name || !code || !city || !street) {
      setError('יש למלא את כל השדות הנדרשים');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const request: CreateKindergartenRequest = {
        name: name.trim(),
        code: code.trim(),
        city: city.trim(),
        street: street.trim(),
        houseNumber: houseNumber.trim() || undefined,
        phone: phone.trim() || undefined,
        phone2: phone2.trim() || undefined,
        maxChildren,
        organizationPublicId: orgFilter || undefined,
        gpsLatitude,
        gpsLongitude,
        photosBase64: photosBase64.length > 0 ? photosBase64 : undefined,
        openTime,
        closeTime,
        displaySwitchTime,
        latestArrivalTime,
        networkType: networkType || undefined,
        networkName: networkName.trim() || undefined,
        networkPassword: networkPassword.trim() || undefined,
        internetProvider: internetProvider.trim() || undefined,
        internetPlanDetails: internetPlanDetails.trim() || undefined,
        routerModel: routerModel.trim() || undefined,
        tabletModel: tabletModel.trim() || undefined,
      };

      const result = await adminApi.createKindergarten(request);
      if (result.success && result.data) {
        onCreated(result.data);
      } else {
        setError(result.message || 'שגיאה ביצירת גן');
      }
    } catch {
      setError('שגיאה ביצירת גן');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 14px',
    fontSize: '15px',
    border: `2px solid ${colors.border}`,
    borderRadius: '10px',
    outline: 'none',
    boxSizing: 'border-box' as const,
    direction: 'rtl' as const,
  };

  const labelStyle = {
    fontSize: '13px',
    fontWeight: '500' as const,
    color: colors.text,
    marginBottom: '6px',
    display: 'block',
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{
          backgroundColor: 'white',
          borderRadius: '24px 24px 0 0',
          padding: '20px',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        dir="rtl"
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.text }}>
            הוספת גן חדש
          </h2>
          <motion.button
            onClick={onClose}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: colors.background,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            whileTap={{ scale: 0.9 }}
          >
            <X size={18} color={colors.textLight} />
          </motion.button>
        </div>

        {/* Error */}
        {error && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px',
            backgroundColor: '#fef2f2',
            borderRadius: '8px',
            marginBottom: '16px',
          }}>
            <AlertCircle size={16} color={colors.error} />
            <span style={{ fontSize: '13px', color: colors.error }}>{error}</span>
          </div>
        )}

        {/* Form */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 2 }}>
              <FormLabel text="שם הגן" required helpText={adminKindergartenHelpTexts.name.tooltip} style={{ color: colors.text }} />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="שם הגן"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <FormLabel text="קוד" required helpText={adminKindergartenHelpTexts.code.tooltip} style={{ color: colors.text }} />
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ABC123"
                maxLength={10}
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'center' }}
              />
              <HelperText text={adminKindergartenHelpTexts.code.helper!} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <FormLabel text="עיר" required helpText={adminKindergartenHelpTexts.city.tooltip} style={{ color: colors.text }} />
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="עיר"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>רחוב *</label>
              <input
                type="text"
                value={street}
                onChange={(e) => setStreet(e.target.value)}
                placeholder="רחוב"
                style={inputStyle}
              />
            </div>
            <div style={{ width: '80px' }}>
              <label style={labelStyle}>מספר</label>
              <input
                type="text"
                value={houseNumber}
                onChange={(e) => setHouseNumber(e.target.value)}
                placeholder="12"
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'center' }}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px' }}>
            <div style={{ flex: 1 }}>
              <FormLabel text="טלפון" helpText={adminKindergartenHelpTexts.phone.tooltip} style={{ color: colors.text }} />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="03-1234567"
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'right' }}
              />
              <HelperText text={adminKindergartenHelpTexts.phone.helper!} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>טלפון נוסף</label>
              <input
                type="tel"
                value={phone2}
                onChange={(e) => setPhone2(e.target.value)}
                placeholder="050-1234567"
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'right' }}
              />
            </div>
            <div style={{ width: '120px' }}>
              <FormLabel text="מקסימום ילדים" helpText={adminKindergartenHelpTexts.capacity.tooltip} style={{ color: colors.text }} />
              <input
                type="number"
                value={maxChildren}
                onChange={(e) => setMaxChildren(parseInt(e.target.value) || 35)}
                style={{ ...inputStyle, direction: 'ltr', textAlign: 'center' }}
              />
              <HelperText text={adminKindergartenHelpTexts.capacity.helper!} />
            </div>
          </div>

          {/* אינטרנט ורשת */}
          <div style={{ padding: '14px', backgroundColor: '#f0f9ff', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ ...labelStyle, marginBottom: '0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Wifi size={14} color={colors.primary} /> אינטרנט ורשת
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>סוג רשת</label>
                <select value={networkType} onChange={(e) => setNetworkType(Number(e.target.value))} style={{ ...inputStyle, cursor: 'pointer' }}>
                  <option value={0}>לא מוגדר</option>
                  <option value={1}>אלחוטי (WiFi)</option>
                  <option value={2}>קווי</option>
                  <option value={3}>משולב</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>ספק אינטרנט</label>
                <input type="text" value={internetProvider} onChange={(e) => setInternetProvider(e.target.value)} placeholder="בזק, הוט..." style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>שם רשת</label>
                <input type="text" value={networkName} onChange={(e) => setNetworkName(e.target.value)} placeholder="SSID" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>סיסמת רשת</label>
                <input type="text" value={networkPassword} onChange={(e) => setNetworkPassword(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div>
              <label style={{ ...labelStyle, fontSize: '12px' }}>פרטי מנוי</label>
              <input type="text" value={internetPlanDetails} onChange={(e) => setInternetPlanDetails(e.target.value)} placeholder="חבילת 100Mbps..." style={inputStyle} />
            </div>
          </div>

          {/* ציוד */}
          <div style={{ padding: '14px', backgroundColor: '#faf5ff', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <label style={{ ...labelStyle, marginBottom: '0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Router size={14} color="#8b5cf6" /> ציוד
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>דגם ראוטר</label>
                <input type="text" value={routerModel} onChange={(e) => setRouterModel(e.target.value)} placeholder="TP-Link, Mikrotik..." style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>דגם טאבלט</label>
                <input type="text" value={tabletModel} onChange={(e) => setTabletModel(e.target.value)} placeholder="Samsung Tab A8..." style={inputStyle} />
              </div>
            </div>
          </div>

          {/* Time Settings */}
          <div style={{ padding: '12px', backgroundColor: colors.background, borderRadius: '12px' }}>
            <label style={{ ...labelStyle, marginBottom: '12px', fontWeight: '600' }}>שעות פעילות</label>
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <FormLabel text="שעת פתיחה" helpText={adminKindergartenHelpTexts.defaultOpenTime.tooltip} style={{ color: colors.text, fontSize: '12px' }} />
                <input
                  type="time"
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                  style={{ ...inputStyle, fontSize: '14px' }}
                />
                <HelperText text={adminKindergartenHelpTexts.defaultOpenTime.helper!} />
              </div>
              <div style={{ flex: 1 }}>
                <FormLabel text="שעת סגירה" helpText={adminKindergartenHelpTexts.defaultCloseTime.tooltip} style={{ color: colors.text, fontSize: '12px' }} />
                <input
                  type="time"
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                  style={{ ...inputStyle, fontSize: '14px' }}
                />
                <HelperText text={adminKindergartenHelpTexts.defaultCloseTime.helper!} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>שעת החלפת מסך</label>
                <input
                  type="time"
                  value={displaySwitchTime}
                  onChange={(e) => setDisplaySwitchTime(e.target.value)}
                  style={{ ...inputStyle, fontSize: '14px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, fontSize: '12px' }}>שעת הגעה אחרונה</label>
                <input
                  type="time"
                  value={latestArrivalTime}
                  onChange={(e) => setLatestArrivalTime(e.target.value)}
                  style={{ ...inputStyle, fontSize: '14px' }}
                />
              </div>
            </div>
          </div>

          {/* Photo Upload - Multi */}
          <PhotoUpload
            mode="multi"
            newPhotoPreviews={photoPreviews}
            onPhotosAdd={(base64List) => {
              setPhotosBase64(prev => [...prev, ...base64List]);
              setPhotoPreviews(prev => [...prev, ...base64List.map(b => `data:image/jpeg;base64,${b}`)]);
            }}
            onPhotoRemove={() => {}}
            onNewPhotoRemove={(index) => {
              setPhotosBase64(prev => prev.filter((_, i) => i !== index));
              setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
            }}
          />

          {/* Map / GPS */}
          <KindergartenMap
            latitude={gpsLatitude}
            longitude={gpsLongitude}
            name={name || 'גן חדש'}
            address={`${street} ${houseNumber || ''}, ${city}`}
            editable
            onLocationSelect={(lat, lng) => {
              setGpsLatitude(lat);
              setGpsLongitude(lng);
            }}
          />
        </div>

        {/* Submit Button */}
        <motion.button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            width: '100%',
            marginTop: '24px',
            padding: '14px',
            borderRadius: '12px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, #4f46e5 100%)`,
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            border: 'none',
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
          }}
          whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
        >
          {isSubmitting ? 'יוצר...' : 'הוסף גן'}
        </motion.button>
      </motion.div>
    </motion.div>
  );
}
